import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Car, Pencil } from "lucide-react";
import { toast } from "sonner";
import { AuthImage } from "@/components/AuthImage";
import { FileUploadButton } from "@/components/FileUploadButton";
import { TravelerAssign, TravelerChips } from "@/components/TravelerAssign";
import { formatError } from "@/lib/api";
import { useCollection } from "./useCollection";

const empty = { company: "", pickup_location: "", return_location: "", pickup_at: "", return_at: "", confirmation: "", car_model: "", reservation_file_id: null, traveler_ids: [], notes: "" };

export default function CarsSection({ tripId, onChange }) {
  const { items, loading, create, update, remove } = useCollection(tripId, "cars", onChange);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  function openNew() { setEditingId(null); setForm(empty); setOpen(true); }
  function openEdit(item) {
    setEditingId(item.id);
    setForm({ ...empty, ...Object.fromEntries(Object.entries(item).filter(([k]) => k in empty)) });
    setOpen(true);
  }
  function handleOpenChange(v) { setOpen(v); if (!v) { setEditingId(null); setForm(empty); } }

  async function save() {
    if (!form.company) { toast.error("Rental company required"); return; }
    setSaving(true);
    try {
      if (editingId) { await update(editingId, form); toast.success("Car rental updated"); }
      else { await create(form); toast.success("Car rental added"); }
      handleOpenChange(false);
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  }

  return (
    <div className="mt-6 space-y-4">
      <div className="flex justify-between items-center">
        <div><h3 className="font-display text-xl">Car rentals</h3><p className="text-sm text-stone-500">Vehicles and pickup details.</p></div>
        <Button className="rounded-full bg-primary text-primary-foreground" onClick={openNew} data-testid="add-car-btn"><Plus className="w-4 h-4 mr-2" />Add rental</Button>
        <Dialog open={open} onOpenChange={handleOpenChange}>
          <DialogContent className="rounded-2xl max-w-lg">
            <DialogHeader><DialogTitle className="font-display">{editingId ? "Edit car rental" : "New car rental"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Company</Label><Input className="rounded-xl" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="car-company-input" /></div>
              <div><Label>Car model</Label><Input className="rounded-xl" value={form.car_model} onChange={(e) => setForm({ ...form, car_model: e.target.value })} /></div>
              <div><Label>Pickup location</Label><Input className="rounded-xl" value={form.pickup_location} onChange={(e) => setForm({ ...form, pickup_location: e.target.value })} /></div>
              <div><Label>Return location</Label><Input className="rounded-xl" value={form.return_location} onChange={(e) => setForm({ ...form, return_location: e.target.value })} /></div>
              <div><Label>Pickup at</Label><Input type="text" placeholder="YYYY-MM-DD HH:MM" title="Format: YYYY-MM-DD HH:MM" className="rounded-xl" value={form.pickup_at || ""} onChange={(e) => setForm({ ...form, pickup_at: e.target.value })} /></div>
              <div><Label>Return at</Label><Input type="text" placeholder="YYYY-MM-DD HH:MM" title="Format: YYYY-MM-DD HH:MM" className="rounded-xl" value={form.return_at || ""} onChange={(e) => setForm({ ...form, return_at: e.target.value })} /></div>
              <div className="col-span-2"><Label>Confirmation</Label><Input className="rounded-xl" value={form.confirmation} onChange={(e) => setForm({ ...form, confirmation: e.target.value })} /></div>
              <div className="col-span-2 flex items-center gap-3"><FileUploadButton label={form.reservation_file_id ? "Change photo" : "Attach reservation"} testId="car-file-upload" onUploaded={(id) => setForm((f) => ({ ...f, reservation_file_id: id }))} />{form.reservation_file_id && <span className="text-xs text-stone-500">Attached</span>}</div>
              <div className="col-span-2 pt-1"><TravelerAssign tripId={tripId} value={form.traveler_ids} onChange={(ids) => setForm((f) => ({ ...f, traveler_ids: ids }))} testId="car-travelers" /></div>
            </div>
            <DialogFooter><Button variant="ghost" onClick={() => handleOpenChange(false)} className="rounded-full">Cancel</Button><Button onClick={save} disabled={saving} className="rounded-full bg-primary text-primary-foreground" data-testid="save-car-btn">{saving ? "Saving..." : editingId ? "Save changes" : "Save"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {loading ? <div className="h-32 rounded-2xl bg-muted animate-pulse" /> :
       items.length === 0 ? (
        <Card className="rounded-2xl p-10 border-dashed border-border text-center" data-testid="empty-cars"><Car className="w-8 h-8 mx-auto text-primary" /><p className="mt-2 text-stone-500">No car rentals yet.</p></Card>
       ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((c) => (
            <Card key={c.id} className="p-5 rounded-2xl border-border" data-testid={`car-${c.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-primary"><Car className="w-4 h-4" /><span className="font-display text-lg">{c.company}</span>{c.car_model && <span className="text-stone-500">· {c.car_model}</span>}</div>
                <div className="flex items-center gap-0.5">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(c)} className="rounded-full text-stone-400 hover:text-primary" data-testid={`edit-car-${c.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(c.id)} className="rounded-full text-stone-400 hover:text-destructive" data-testid={`delete-car-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
              <div className="mt-2 text-xs text-stone-500 grid grid-cols-2 gap-1">
                {c.pickup_location && <div>Pickup: {c.pickup_location}</div>}
                {c.return_location && <div>Return: {c.return_location}</div>}
                {c.pickup_at && <div>At: {c.pickup_at}</div>}
                {c.return_at && <div>Until: {c.return_at}</div>}
                {c.confirmation && <div className="col-span-2">Conf: {c.confirmation}</div>}
              </div>
              {c.reservation_file_id && <AuthImage fileId={c.reservation_file_id} className="mt-3 w-full h-32 object-cover rounded-xl" />}
              <TravelerChips tripId={tripId} travelerIds={c.traveler_ids} />
            </Card>
          ))}
        </div>
       )}
    </div>
  );
}
